import metricsStore from '@src/globalStore/metricsStore';
import { humanize } from '@src/utils';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { getMetricDisplayName, getMetricShortName, getMetricUnitByKey } from '@src/utils/metrics';
import { FLOW_METRICS_DISPLAY } from '@src/utils/flow/meta';
import moment from 'moment-timezone';
import _ from 'lodash';

const DELETED_METRIC = '[DELETED METRIC]';
const DELETED_DIMENSION = 'DELETED_DIMENSION';

export const getLocalTime = (date, tzOffset = null, timeFormat = 'HH:mm ll') => {
  return moment(date).format(timeFormat);
};

const getAlertEvents = (alert) => {
  const { alertEvents, metricName } = alert;
  const metricUnit = getMetricUnitByKey({
    metricKey: metricName,
  });
  return alertEvents.map((e) => ({
    ...e,
    peakSeverity: e.eventType,
    cumulativeImpactedUniquesDisplay: e.cumulativeImpactedUniques
      ? humanize(e.cumulativeImpactedUniques, '')
      : 'NA',
    eventTime: e.eventTimestamp ? getLocalTime(new Date(e.eventTimestamp * 1000)) : null,
    peakValue: e.value,
    peakValueDisplay: humanize(Number(e.value), metricUnit),
    duration: null,
    graphTime: moment.utc(e.eventTimestamp * 1000).format(),
  }));
};

const getRootCauseDetails = ({ alert, dimensionListObject = {}, dimensionMapping = {} }) => {
  const { dimensionName, rootCauseDisplayLabel } = alert;

  const rootCauseArray = rootCauseDisplayLabel.split('&&&');
  const dimensionNameArray = dimensionName.split('&&&');
  let rootCauseDisplay = '';
  let areAllRCDimensionsAvailable = true;
  rootCauseArray.forEach((element, index) => {
    const pulse5DimensionMapping = dimensionMapping[dimensionNameArray[index]];

    const dimensionLabel = pulse5DimensionMapping
      ? dimensionListObject[pulse5DimensionMapping.field] || pulse5DimensionMapping.pulse5Label
      : '';

    rootCauseDisplay +=
      index !== 0
        ? ' and ' + `${element} (${dimensionLabel ? dimensionLabel : DELETED_DIMENSION})`
        : `${element} (${dimensionLabel ? dimensionLabel : DELETED_DIMENSION})`;

    if (!dimensionLabel) {
      areAllRCDimensionsAvailable = false;
    }
  });

  return { rootCauseDisplay, areAllRCDimensionsAvailable };
};

const getAlertDuration = (alert) => {
  const { startTimestamp, endTimestamp } = alert;
  const start = moment(startTimestamp * 1000);
  const end = moment(endTimestamp * 1000);
  const difference = end.diff(start); // The difference in milliseconds
  const duration = moment.duration(difference);
  const durationDisplay =
    difference >= 0
      ? `${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`
      : alert.alertType === 'AD_ALERT' || !alert.hasEnded || difference < 0
        ? 'NA'
        : null;

  return durationDisplay;
};

const getAlertStatus = (alert) => {
  const { hasEnded, alertType } = alert;
  return hasEnded !== undefined && hasEnded !== null
    ? alertType === 'AD_ALERT'
      ? 'NA'
      : hasEnded
        ? 'Ended'
        : 'Active'
    : null;
};

export const formatAIAlertSummary = (alerts, dimensionListObject = {}, dimensionMapping = {}) => {
  return (alerts || [])?.map((alert) => {
    const {
      metricName,
      startTimestamp,
      endTimestamp,
      eventTimestamp,
      cumulativeImpactedUniques,
      cumulativeTotalUniques,
      peakValue,
    } = alert;

    const { allMetricKeys } = metricsStore.getState();
    const isDeletedMetric = !allMetricKeys[CURRENT_PRODUCT_MODE.toLowerCase()].includes(metricName);

    const { rootCauseDisplay, areAllRCDimensionsAvailable } = getRootCauseDetails({
      alert,
      dimensionListObject,
      dimensionMapping,
    });

    const metricUnit = getMetricUnitByKey({
      metricKey: metricName,
    });

    return {
      ...alert,
      metricName: isDeletedMetric ? DELETED_METRIC : metricName,
      cumulativeImpactedUniquesDisplay: cumulativeImpactedUniques
        ? humanize(cumulativeImpactedUniques, '')
        : 'NA',
      cumulativeTotalUniquesDisplay: cumulativeTotalUniques
        ? humanize(cumulativeTotalUniques, '')
        : 'NA',
      startTime: startTimestamp ? getLocalTime(new Date(startTimestamp * 1000)) : null,
      endTime: endTimestamp ? getLocalTime(new Date(endTimestamp * 1000)) : null,
      startGraphTime: moment.utc(startTimestamp * 1000).format(),
      eventTime: eventTimestamp ? getLocalTime(new Date(eventTimestamp * 1000)) : null,
      rootCauseDisplay,
      metricDisplayName: getMetricDisplayName({
        metricKey: metricName,
        defaultValue: DELETED_METRIC,
      }),
      isDeletedMetric: isDeletedMetric,
      areAllRCDimensionsAvailable,
      metricUnit: metricUnit, // fallback is ''
      peakValueDisplay: humanize(Number(peakValue), metricUnit),
      duration: getAlertDuration(alert),
      alertEvents: getAlertEvents(alert),
      peakEvent: getAlertEvents(alert).find((alertEvent) => alertEvent.peakValue === peakValue),
      status: getAlertStatus(alert),
    };
  });
};

export const getEcoMetricCategoryMap = (alerts) => {
  const metricAlertsCountMap: any = {
    'Flow Metrics': {},
    'Custom Metrics': [],
    'Standard Metrics': [],
  };
  const { userDefinedMetricsMeta, flowConfigMeta } = metricsStore.getState();
  const customMetrics = userDefinedMetricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];

  // Get metric counts from alerts
  const categoryMap = alerts.reduce((acc, alert) => {
    const category = alert.metricDisplayName === DELETED_METRIC ? DELETED_METRIC : alert.metricName;
    if (!acc[category]) {
      acc[category] = 0;
    }
    acc[category]++;
    return acc;
  }, {});

  for (const metricKey in categoryMap) {
    const metricAlertsCount = categoryMap[metricKey];
    if (metricAlertsCount) {
      if (customMetrics?.[metricKey]) {
        const flowId = customMetrics[metricKey].flowID;
        if (flowId) {
          const invertFlowMetricMap = _.invert(flowConfigMeta[flowId]?.metrics);
          const flowName = flowConfigMeta[flowId].name;
          metricAlertsCountMap['Flow Metrics'][flowName] = [
            ...(metricAlertsCountMap['Flow Metrics'][flowName] || []),
            {
              metric: metricKey,
              count: metricAlertsCount,
              label: FLOW_METRICS_DISPLAY[invertFlowMetricMap[metricKey]]?.name,
            },
          ];
        } else
          metricAlertsCountMap['Custom Metrics'].push({
            metric: metricKey,
            count: metricAlertsCount,
            label: getMetricShortName({ metricKey }),
          });
      } else
        metricAlertsCountMap['Standard Metrics'].push({
          metric: metricKey,
          count: metricAlertsCount,
          label: getMetricShortName({ metricKey }),
        });
    }
  }

  Object.keys(metricAlertsCountMap['Flow Metrics']).forEach((key) =>
    metricAlertsCountMap['Flow Metrics'][key].sort((a, b) => b.count - a.count),
  );
  metricAlertsCountMap['Custom Metrics'].sort((a, b) => b.count - a.count);
  metricAlertsCountMap['Standard Metrics'].sort((a, b) => b.count - a.count);

  return metricAlertsCountMap;
};

export const getFilteredAlertsByMetricCategory = (alerts, selectedCategories) => {
  if (!selectedCategories || selectedCategories.length === 0) {
    return alerts;
  }

  const { userDefinedMetricsMeta } = metricsStore.getState();
  const customMetrics = userDefinedMetricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];

  return alerts.filter((alert) => {
    const metricKey = alert.metricName;

    // Check if it's a custom metric with flow
    const isFlowMetric = customMetrics?.[metricKey] && customMetrics[metricKey].flowID;

    // Check if it's a custom metric without flow
    const isCustomMetric = customMetrics?.[metricKey] && !customMetrics[metricKey].flowID;

    // Check if it's a standard metric
    const isStandardMetric = !customMetrics?.[metricKey];

    return (
      (selectedCategories.includes('Flow Metrics') && isFlowMetric) ||
      (selectedCategories.includes('Custom Metrics') && isCustomMetric) ||
      (selectedCategories.includes('Standard Metrics') && isStandardMetric)
    );
  });
};
